import React, { useEffect, useMemo, useRef, useState } from "react"
import { CreditCard, User } from "lucide-react"
import Accordion from "./Accordion.jsx"
import InputField from "./InputField.jsx"
import CustomerSearchInput from "./CustomerSearchInput.jsx"
import CustomerChoicePrompt from "./CustomerChoicePrompt.jsx"
import { FieldChangeHint } from "./FieldChangeHint.jsx"
import { OrderFormCheckbox } from "./EnterKeyControls.jsx"
import { ContactService } from "../services/contactService.js"
import {
    billingFieldsFromApiCustomer,
    billingFieldsFromShippingValues,
    shippingFieldsFromShipTo,
} from "./shippingAddressUtils.js"
import { useRegionOptions } from "./useRegionOptions.js"
import { useCountries } from "./useCountries.js"
import { isBillingSectionComplete } from "./sectionCompletion.js"
import { focusOrderFormNextButton } from "./orderFormFocusUtils.js"
import {
    ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
} from "./orderFormAccordionConstants.js"
import { focusElement } from "../utils/scrollFocusedElement.js"
import { NAME_COMMA_ERROR, nameHasComma } from "./nameCommaValidation.js"
import {
    ADDRESS_PERSON_MIRROR_KEYS,
    addressHasAnyDetail,
    addressHasEdits,
    getAddressFieldDiffs,
    getAddressPersonFieldDiffs,
    snapshotAddressBaseline,
    undoAddressField,
} from "./customerFormBaseline.js"

function formatPhone(value) {
    return ContactService.formatPhone(value)
}

function contactLikeFromProps(contact) {
    return {
        firstName: contact.firstName ?? "",
        lastName: contact.lastName ?? "",
        companyName: contact.companyName ?? "",
        phone: contact.phone ?? "",
        email: contact.email ?? "",
    }
}

function contactPersonMirrorValues(contact) {
    const base = contactLikeFromProps(contact)
    return {
        ...base,
        phone: formatPhone(base.phone) || base.phone,
    }
}

function emptyManualBillingFields() {
    return {
        firstName: "",
        lastName: "",
        companyName: "",
        email: "",
        country: "",
        province: "",
        city: "",
        streetAddress: "",
        postalCode: "",
        phone: "",
        defaultBillAddressId: null,
    }
}

function applyBillingCustomerLoad(prev, fields, billingCustomerId, defaultBillAddressId) {
    const next = {
        ...prev,
        ...fields,
        billingCustomerId,
        defaultBillAddressId,
        addressDirty: false,
        useShippingAddress: false,
        attachAddressToCustomer: false,
    }
    next.addressBaseline = snapshotAddressBaseline(next)
    return next
}

function clearBillingCustomer(prev) {
    return {
        ...prev,
        ...emptyManualBillingFields(),
        billingCustomerId: null,
        defaultBillAddressId: null,
        addressDirty: false,
        useShippingAddress: false,
        attachAddressToCustomer: true,
        addressBaseline: null,
    }
}

/** @param {Record<string, unknown>} v */
function firstMissingBillingFieldId(v) {
    if (String(v.country ?? "").trim() === "") {
        return "billing-country"
    }
    if (String(v.firstName ?? "").trim() === "") {
        return "billing-first-name"
    }
    if (String(v.lastName ?? "").trim() === "") {
        return "billing-last-name"
    }
    if (String(v.province ?? "").trim() === "") {
        return "billing-province"
    }
    if (String(v.city ?? "").trim() === "") {
        return "billing-city"
    }
    if (String(v.streetAddress ?? "").trim() === "") {
        return "billing-street"
    }
    if (String(v.postalCode ?? "").trim() === "") {
        return "billing-postal"
    }
    if (String(v.phone ?? "").trim() === "") {
        return "billing-phone"
    }
    if (String(v.email ?? "").trim() === "") {
        return "billing-email"
    }
    return "billing-country"
}

/** Same rules as auto-check for “Same as shipping address” (section load / reset). */
function computeShouldAutoSameAsShipping(contact, shipping) {
    const shippingOnMain =
        contact.customerId != null &&
        shipping.shippingCustomerId != null &&
        Number(shipping.shippingCustomerId) === Number(contact.customerId)
    return (
        contact.customerId == null ||
        (Boolean(contact.bill_same_as_ship) && shippingOnMain)
    )
}

function expectedBillingCustomerIdForSameAsShipping(shipping, contact) {
    if (contact.customerId != null) {
        return contact.customerId
    }
    return shipping.shippingCustomerId ?? null
}

function billingMatchesShippingSnapshot(b, shipping, contact) {
    const c = billingFieldsFromShippingValues(shipping)
    const bid = expectedBillingCustomerIdForSameAsShipping(shipping, contact)
    return (
        String(b.firstName ?? "") === c.firstName &&
        String(b.lastName ?? "") === c.lastName &&
        String(b.companyName ?? "") === c.companyName &&
        String(b.email ?? "") === c.email &&
        String(b.country ?? "") === c.country &&
        String(b.province ?? "") === c.province &&
        String(b.city ?? "") === c.city &&
        String(b.streetAddress ?? "") === c.streetAddress &&
        String(b.postalCode ?? "") === c.postalCode &&
        String(b.phone ?? "") === c.phone &&
        (b.billingCustomerId == null ?
            bid == null
            : Number(b.billingCustomerId) === Number(bid))
    )
}

/**
 * @param {{
 *   value: Record<string, unknown>,
 *   shipping: Record<string, unknown>,
 *   delivery?: { method?: string, pickupLocationId?: number | null },
 *   contact: Record<string, unknown> & {
 *     customerId?: number | null,
 *     bill_to?: unknown,
 *     bill_same_as_ship?: boolean,
 *   },
 *   onChange: (v: Record<string, unknown> | ((p: Record<string, unknown>) => Record<string, unknown>)) => void,
 *   onContactChange?: (v: Record<string, unknown> | ((p: Record<string, unknown>) => Record<string, unknown>)) => void,
 *   onNext: () => void,
 *   title?: string,
 *   isOpen?: boolean,
 *   touched?: boolean,
 *   statusLabel?: string | null,
 *   disabled?: boolean,
 *   onHeaderClick?: () => void,
 *   isActive?: boolean,
 *   onJumpToSection?: (index: number) => void,
 *   activationFocusMode?: "first" | "nextRequired" | null,
 *   onActivationFocusConsumed?: () => void,
 *   activationFocusDelayMs?: number,
 *   fieldErrors?: Record<string, string> | null,
 *   showFieldErrors?: boolean,
 *   hasErrors?: boolean,
 * }} props
 */
export default function BillingSection({
    value,
    shipping,
    delivery,
    contact,
    onChange,
    onContactChange,
    onNext,
    title = "4. Billing Information",
    isOpen,
    touched = false,
    statusLabel = null,
    disabled = false,
    onHeaderClick,
    isActive = false,
    onJumpToSection,
    activationFocusMode = null,
    onActivationFocusConsumed,
    activationFocusDelayMs = 350,
    fieldErrors = null,
    showFieldErrors = false,
    hasErrors = false,
}) {
    const nextButtonRef = useRef(/** @type {HTMLButtonElement | null} */(null))
    const [billingSearchQuery, setBillingSearchQuery] = useState("")
    const [billingSearchClearNonce, setBillingSearchClearNonce] = useState(0)
    const [billingSearchFocusNonce, setBillingSearchFocusNonce] = useState(0)
    const [emphasizeNext, setEmphasizeNext] = useState(false)
    const valueRef = useRef(value)
    valueRef.current = value
    const shippingRef = useRef(shipping)
    shippingRef.current = shipping

    const userOverrideSameAsShippingRef = useRef(false)
    const prevShouldAutoSameAsShippingRef = useRef(/** @type {boolean | null} */(null))

    const prevContactCustomerIdRef = useRef(/** @type {number | null} */(null))
    const prevContactSyncKeyRef = useRef(/** @type {string | null} */(null))
    /** Keys the user edited on billing; stop contact→bill mirror for those. */
    const contactPersonOverwriteKeysRef = useRef(/** @type {Set<string>} */(new Set()))
    const softContactPersonMirrorActiveRef = useRef(false)
    const contactSyncKey =
        `${contact.customerId ?? ""}|${JSON.stringify(contact.bill_to ?? null)}|` +
        `${contact.firstName ?? ""}|${contact.lastName ?? ""}|${contact.email ?? ""}|${contact.phone ?? ""}|` +
        `${contact.companyName ?? ""}|${contact.default_bill_id ?? ""}`

    function resetContactPersonSoftMirror() {
        contactPersonOverwriteKeysRef.current = new Set()
        softContactPersonMirrorActiveRef.current = false
    }

    const { countries, countryNames } = useCountries()
    const regionOptions = useRegionOptions(String(value.country ?? ""), countries)
    const isCompleted = isBillingSectionComplete(value) && Boolean(touched)

    const mainCustomerDisplayLabel =
        String(contact.selectedCustomerLabel ?? "").trim() ||
        [contact.firstName, contact.lastName].filter(Boolean).join(" ").trim() ||
        String(contact.email ?? "").trim() ||
        String(contact.phone ?? "").trim()

    const isPickupDelivery = String(delivery?.method ?? "") === "pickup"

    const shouldAutoSameAsShipping = useMemo(
        () =>
            !isPickupDelivery &&
            computeShouldAutoSameAsShipping(contact, shipping),
        [
            isPickupDelivery,
            contact.customerId,
            contact.bill_same_as_ship,
            shipping.shippingCustomerId,
        ]
    )

    const prevAutoSameAsShipRef = useRef(shouldAutoSameAsShipping)
    const prevContactIdForSameAsShipRef = useRef(contact.customerId)

    /** Pickup has no shipping address — never keep bill-same-as-ship. */
    useEffect(() => {
        if (!isPickupDelivery) {
            return
        }
        onChange((p) =>
            p.useShippingAddress ? { ...p, useShippingAddress: false } : p
        )
    }, [isPickupDelivery, onChange])

    /** Main contact cleared in section 1 — stop mirroring shipping; keep address fields. */
    useEffect(() => {
        const prev = prevContactIdForSameAsShipRef.current
        prevContactIdForSameAsShipRef.current = contact.customerId
        if (prev == null || contact.customerId != null) {
            return
        }
        userOverrideSameAsShippingRef.current = true
        onChange((p) =>
            p.useShippingAddress ? { ...p, useShippingAddress: false } : p
        )
    }, [contact.customerId, onChange])

    useEffect(() => {
        const prev = prevShouldAutoSameAsShippingRef.current
        if (prev !== null && prev !== shouldAutoSameAsShipping) {
            userOverrideSameAsShippingRef.current = false
        }
        prevShouldAutoSameAsShippingRef.current = shouldAutoSameAsShipping
    }, [shouldAutoSameAsShipping])

    /** End auto “same as shipping” when conditions flip true → false (not manual mode). */
    useEffect(() => {
        const was = prevAutoSameAsShipRef.current
        prevAutoSameAsShipRef.current = shouldAutoSameAsShipping
        if (was !== true || shouldAutoSameAsShipping !== false) {
            return
        }
        if (userOverrideSameAsShippingRef.current) {
            return
        }
        onChange((prev) => {
            if (!prev.useShippingAddress) {
                return prev
            }
            return { ...prev, useShippingAddress: false }
        })
    }, [shouldAutoSameAsShipping, onChange])

    useEffect(() => {
        if (!shouldAutoSameAsShipping) {
            return
        }
        if (userOverrideSameAsShippingRef.current) {
            return
        }
        onChange((prev) => {
            if (prev.useShippingAddress) {
                return prev
            }
            const s = shippingRef.current
            return {
                ...prev,
                ...billingFieldsFromShippingValues(s),
                useShippingAddress: true,
                billingCustomerId:
                    expectedBillingCustomerIdForSameAsShipping(s, contact),
                defaultBillAddressId: null,
                addressDirty: false,
            }
        })
    }, [shouldAutoSameAsShipping, contact.customerId, onChange])

    useEffect(() => {
        // Ensure manual overrides / autofill stay formatted consistently.
        const raw = value.phone ?? ""
        const formatted = formatPhone(raw)
        if (formatted && formatted !== raw) {
            onChange((p) => ({ ...p, phone: formatted }))
        }

    }, [value.phone])

    /** Keep billing fields equal to shipping while “same as shipping” is on. */
    useEffect(() => {
        if (!value.useShippingAddress) {
            return
        }
        const s = shippingRef.current
        onChange((prev) => {
            if (!prev.useShippingAddress) {
                return prev
            }
            if (billingMatchesShippingSnapshot(prev, s, contact)) {
                return prev
            }
            return {
                ...prev,
                ...billingFieldsFromShippingValues(s),
                billingCustomerId:
                    expectedBillingCustomerIdForSameAsShipping(s, contact),
                defaultBillAddressId: null,
                addressDirty: false,
            }
        })
    }, [
        value.useShippingAddress,
        contact.customerId,
        shipping.firstName,
        shipping.lastName,
        shipping.companyName,
        shipping.email,
        shipping.country,
        shipping.province,
        shipping.city,
        shipping.streetAddress,
        shipping.postalCode,
        shipping.phone,
        shipping.shippingCustomerId,
        onChange,
    ])

    const billingAlignedWithMainCustomer = useMemo(() => {
        if (contact.customerId == null) {
            return false
        }
        if (value.billingCustomerId == null) {
            return false
        }
        if (Number(value.billingCustomerId) !== Number(contact.customerId)) {
            return false
        }
        const q = billingSearchQuery.trim().toLowerCase()
        const label = mainCustomerDisplayLabel.trim().toLowerCase()
        return q === label
    }, [
        contact.customerId,
        value.billingCustomerId,
        billingSearchQuery,
        mainCustomerDisplayLabel,
    ])

    const showResetToMainCustomer =
        contact.customerId != null && !billingAlignedWithMainCustomer

    /** Bind billing to contact’s customer from `bill_to` when not mirroring shipping. */
    useEffect(() => {
        if (shouldAutoSameAsShipping) {
            const cid =
                contact.customerId != null ? Number(contact.customerId) : null
            prevContactCustomerIdRef.current = cid
            return
        }
        const cid =
            contact.customerId != null ? Number(contact.customerId) : null
        const prevContact = prevContactCustomerIdRef.current

        if (cid == null) {
            if (prevContact != null) {
                resetContactPersonSoftMirror()
                onChange((prev) => clearBillingCustomer(prev))
            }
            prevContactCustomerIdRef.current = null
            return
        }

        const bid = valueRef.current.billingCustomerId ?? null
        const bidNum = bid != null ? Number(bid) : null
        const wasOnMain = bidNum == null || bidNum === prevContact

        if (!wasOnMain) {
            prevContactCustomerIdRef.current = cid
            return
        }

        prevContactCustomerIdRef.current = cid
        let cancel = false
            ; (async () => {
                const fields = await shippingFieldsFromShipTo(
                    contact.bill_to ?? null,
                    contactLikeFromProps(contact)
                )
                if (cancel) {
                    return
                }
                onChange((prev) =>
                    applyBillingCustomerLoad(
                        prev,
                        fields,
                        cid,
                        contact.default_bill_id ?? null
                    )
                )
            })()
        return () => {
            cancel = true
        }
    }, [contact.customerId, shouldAutoSameAsShipping, contact.bill_to, onChange])

    /** While billing tracks the main contact customer, keep fields in sync with contact. */
    useEffect(() => {
        if (shouldAutoSameAsShipping) {
            return
        }
        if (contact.customerId == null) {
            return
        }
        if (
            Number(valueRef.current.billingCustomerId) !==
            Number(contact.customerId)
        ) {
            prevContactSyncKeyRef.current = null
            return
        }
        if (prevContactSyncKeyRef.current === contactSyncKey) {
            return
        }
        prevContactSyncKeyRef.current = contactSyncKey
        let cancel = false
            ; (async () => {
                const fields = await shippingFieldsFromShipTo(
                    contact.bill_to ?? null,
                    contactLikeFromProps(contact)
                )
                if (cancel) {
                    return
                }
                onChange((prev) =>
                    applyBillingCustomerLoad(
                        prev,
                        fields,
                        contact.customerId ?? null,
                        contact.default_bill_id ?? null
                    )
                )
            })()
        return () => {
            cancel = true
        }
    }, [
        contactSyncKey,
        shouldAutoSameAsShipping,
        contact.customerId,
        onChange,
    ])

    /**
     * Soft one-way mirror: while creating a new customer and Bill Same As Ship is off
     * (no billing customer), keep billing person fields in sync with contact until the
     * user edits them on billing. Entering this mode always restarts from contact so
     * leftover same-as-ship overrides (e.g. shipping-only first name) are not sticky.
     */
    useEffect(() => {
        const softMirror =
            contact.customerId == null &&
            value.billingCustomerId == null &&
            !value.useShippingAddress
        if (!softMirror) {
            softContactPersonMirrorActiveRef.current = false
            return
        }

        const from = contactPersonMirrorValues(contact)
        if (!softContactPersonMirrorActiveRef.current) {
            softContactPersonMirrorActiveRef.current = true
            contactPersonOverwriteKeysRef.current = new Set()
        }

        onChange((prev) => {
            if (prev.useShippingAddress || prev.billingCustomerId != null) {
                return prev
            }
            let changed = false
            const next = { ...prev }
            for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
                if (contactPersonOverwriteKeysRef.current.has(key)) {
                    continue
                }
                const nextVal = from[key] ?? ""
                if (String(next[key] ?? "") !== String(nextVal)) {
                    next[key] = nextVal
                    changed = true
                }
            }
            return changed ? next : prev
        })
    }, [
        contact.customerId,
        contact.firstName,
        contact.lastName,
        contact.companyName,
        contact.phone,
        contact.email,
        value.billingCustomerId,
        value.useShippingAddress,
        onChange,
    ])

    useEffect(() => {
        if (
            !isActive ||
            activationFocusMode == null ||
            !onActivationFocusConsumed
        ) {
            return
        }
        const mode = activationFocusMode
        const tmr = window.setTimeout(() => {
            if (mode === "first") {
                focusElement(
                    document.getElementById("billing-customer-search-query"),
                    {
                        enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED,
                        delay: true,
                    }
                )
                onActivationFocusConsumed()
                return
            }
            const prioritizeSearch =
                contact.customerId != null &&
                contact.bill_to == null
            if (prioritizeSearch) {
                setEmphasizeNext(false)
                setBillingSearchFocusNonce((n) => n + 1)
                onActivationFocusConsumed()
                return
            }
            const v = valueRef.current
            if (isBillingSectionComplete(v)) {
                setEmphasizeNext(true)
                focusOrderFormNextButton(nextButtonRef)
            } else {
                setEmphasizeNext(false)
                const id = firstMissingBillingFieldId(v)
                focusElement(document.getElementById(id), { enabled: ORDER_FORM_PROGRAMMATIC_SCROLL_ENABLED, delay: true })
            }
            onActivationFocusConsumed()
        }, activationFocusDelayMs)
        return () => window.clearTimeout(tmr)
    }, [
        isActive,
        activationFocusMode,
        activationFocusDelayMs,
        onActivationFocusConsumed,
        contact.customerId,
        contact.bill_to,
    ])

    async function resetToMainCustomer() {
        const cid = contact.customerId
        if (cid == null) {
            return
        }
        const s = shippingRef.current
        if (!isPickupDelivery && computeShouldAutoSameAsShipping(contact, s)) {
            userOverrideSameAsShippingRef.current = false
            onChange((prev) => ({
                ...prev,
                ...billingFieldsFromShippingValues(s),
                useShippingAddress: true,
                billingCustomerId:
                    expectedBillingCustomerIdForSameAsShipping(s, contact),
                defaultBillAddressId: null,
                addressDirty: false,
            }))
            setBillingSearchClearNonce((n) => n + 1)
            return
        }
        userOverrideSameAsShippingRef.current = true
        const fields = await shippingFieldsFromShipTo(
            contact.bill_to ?? null,
            contactLikeFromProps(contact)
        )
        onChange((prev) =>
            applyBillingCustomerLoad(
                prev,
                fields,
                cid,
                contact.default_bill_id ?? null
            )
        )
        setBillingSearchClearNonce((n) => n + 1)
    }

    function handleBillingCustomerSelect(c) {
        userOverrideSameAsShippingRef.current = true
            ; (async () => {
                const fields = await billingFieldsFromApiCustomer(c)
                const merged = applyBillingCustomerLoad(
                    valueRef.current,
                    fields,
                    c.id ?? null,
                    c.default_bill_id ?? null
                )
                onChange(() => merged)
                window.requestAnimationFrame(() => {
                    if (isBillingSectionComplete(merged)) {
                        setEmphasizeNext(true)
                        focusOrderFormNextButton(nextButtonRef)
                    } else {
                        setEmphasizeNext(false)
                        document
                            .getElementById(firstMissingBillingFieldId(merged))
                            ?.focus({ preventScroll: true })
                    }
                })
            })()
    }

    function patchField(patch) {
        onChange((prev) => {
            if (
                contact.customerId == null &&
                prev.billingCustomerId == null &&
                !prev.useShippingAddress
            ) {
                for (const key of ADDRESS_PERSON_MIRROR_KEYS) {
                    if (Object.prototype.hasOwnProperty.call(patch, key)) {
                        contactPersonOverwriteKeysRef.current.add(key)
                    }
                }
            }
            return {
                ...prev,
                ...patch,
                addressDirty:
                    prev.billingCustomerId != null && Object.keys(patch).length > 0 ?
                        true :
                        prev.addressDirty,
            }
        })
    }

    function undoField(key) {
        onChange((prev) => {
            if (
                contact.customerId == null &&
                prev.billingCustomerId == null &&
                !prev.useShippingAddress
            ) {
                contactPersonOverwriteKeysRef.current.delete(key)
                const from = contactPersonMirrorValues(contact)
                return { ...prev, [key]: from[key] ?? "" }
            }
            const next = undoAddressField(prev, prev.addressBaseline, key)
            if (!addressHasEdits(next, next.addressBaseline)) {
                next.addressDirty = false
            }
            return next
        })
    }

    function applyFieldToContact(key) {
        if (onContactChange == null) {
            return
        }
        const raw = valueRef.current[key] ?? ""
        const nextVal = key === "phone" ? (formatPhone(raw) || raw) : raw
        contactPersonOverwriteKeysRef.current.delete(key)
        onContactChange((prev) => ({ ...prev, [key]: nextVal }))
    }

    const softContactPersonMirror =
        contact.customerId == null &&
        value.billingCustomerId == null &&
        !value.useShippingAddress
    const addressBaseline = value.addressBaseline ?? null
    const contactPersonBaseline = useMemo(
        () =>
            softContactPersonMirror ? contactPersonMirrorValues(contact) : null,
        [
            softContactPersonMirror,
            contact.firstName,
            contact.lastName,
            contact.companyName,
            contact.phone,
            contact.email,
        ]
    )
    const fieldDiffs = useMemo(
        () => {
            if (!value.useShippingAddress && value.billingCustomerId != null) {
                return getAddressFieldDiffs(value, addressBaseline)
            }
            if (softContactPersonMirror) {
                return getAddressPersonFieldDiffs(value, contactPersonBaseline)
            }
            return []
        },
        [
            value,
            addressBaseline,
            softContactPersonMirror,
            contactPersonBaseline,
        ]
    )
    const diffsByKey = useMemo(() => {
        /** @type {Record<string, (typeof fieldDiffs)[number]>} */
        const map = {}
        for (const d of fieldDiffs) {
            map[d.key] = d
        }
        return map
    }, [fieldDiffs])
    const showUpdateDefaultPrompt =
        !value.useShippingAddress &&
        value.billingCustomerId != null &&
        fieldDiffs.length > 0
    const showSetDefaultPrompt =
        !value.useShippingAddress &&
        value.billingCustomerId == null &&
        addressHasAnyDetail(value)

    function labelHint(key) {
        const d = diffsByKey[key]
        if (!d) {
            return null
        }
        return (
            <FieldChangeHint
                fromDisplay={d.fromDisplay}
                onUndo={() => undoField(key)}
                {...(softContactPersonMirror && onContactChange != null ?
                    {
                        applyLabel: "Apply to contact",
                        onApply: () => applyFieldToContact(key),
                    } :
                    {})}
            />
        )
    }

    function handleSameAsShippingChange(checked) {
        if (isPickupDelivery) {
            onChange((prev) =>
                prev.useShippingAddress ? { ...prev, useShippingAddress: false } : prev
            )
            return
        }
        if (checked) {
            userOverrideSameAsShippingRef.current = false
            softContactPersonMirrorActiveRef.current = false
            const s = shippingRef.current
            onChange((prev) => ({
                ...prev,
                ...billingFieldsFromShippingValues(s),
                useShippingAddress: true,
                billingCustomerId:
                    expectedBillingCustomerIdForSameAsShipping(s, contact),
                defaultBillAddressId: null,
                addressDirty: false,
            }))
            setBillingSearchClearNonce((n) => n + 1)
            return
        }
        if (shouldAutoSameAsShipping) {
            userOverrideSameAsShippingRef.current = true
        }
        // Leave soft-mirror inactive so the effect re-enters and restarts from contact.
        softContactPersonMirrorActiveRef.current = false
        contactPersonOverwriteKeysRef.current = new Set()
        onChange((prev) => ({ ...prev, useShippingAddress: false }))
    }

    const fieldsLocked = Boolean(value.useShippingAddress)

    return (
        <Accordion
            title={title}
            isActive={isOpen ?? isActive}
            isCompleted={isCompleted}
            statusLabel={statusLabel}
            disabled={disabled}
            onClick={onHeaderClick}
            hasErrors={hasErrors}
        >
            <div className="row g-4">
                <div className="col-12">
                    <div className="p-3 rounded" style={{ backgroundColor: "#e7f3ff" }}>
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
                            <h3
                                className="fw-semibold mb-0 d-flex align-items-center"
                                style={{ color: "#1e3a8a", fontSize: "1rem" }}
                            >
                                <User className="me-2" size={20} />
                                Load Billing Address from Customer
                            </h3>
                            {billingAlignedWithMainCustomer ? (
                                <span
                                    className="text-end"
                                    style={{
                                        color: "#1e40af",
                                        maxWidth: "20rem",
                                        fontSize: "0.875rem",
                                    }}
                                >
                                    Auto-filling from{" "}
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 align-baseline border-0"
                                        style={{
                                            color: "#1e40af",
                                            fontSize: "inherit",
                                            textDecoration: "underline",
                                        }}
                                        onClick={() => onJumpToSection?.(0)}
                                    >
                                        Contact Information
                                    </button>
                                </span>
                            ) : showResetToMainCustomer ? (
                                <button
                                    type="button"
                                    className="btn btn-link p-0 border-0 shipping-reset-main-customer-btn"
                                    onClick={() => resetToMainCustomer()}
                                >
                                    Reset to main customer
                                </button>
                            ) : null}
                        </div>
                        <CustomerSearchInput
                            idPrefix="billing-customer-search"
                            placeholder="Search customer for billing address..."
                            selectedCustomerId={/** @type {number | null} */ (value.billingCustomerId ?? null)}
                            selectedDisplayLabel={
                                contact.customerId != null &&
                                    value.billingCustomerId != null &&
                                    Number(value.billingCustomerId) ===
                                    Number(contact.customerId)
                                    ? mainCustomerDisplayLabel
                                    : null
                            }
                            clearQueryNonce={billingSearchClearNonce}
                            focusNonce={billingSearchFocusNonce}
                            onClearSelection={() => {
                                userOverrideSameAsShippingRef.current = true
                                resetContactPersonSoftMirror()
                                onChange((prev) => clearBillingCustomer(prev))
                            }}
                            onSelect={handleBillingCustomerSelect}
                            onSearchQueryChange={setBillingSearchQuery}
                        />
                    </div>
                </div>

                {!isPickupDelivery ? (
                    <div className="col-12">
                        <div className="form-check">
                            <OrderFormCheckbox
                                className="form-check-input"
                                id="billing-same-as-shipping"
                                checked={Boolean(value.useShippingAddress)}
                                onCheckedChange={(checked) =>
                                    handleSameAsShippingChange(Boolean(checked))
                                }
                            />
                            <label
                                className="form-check-label"
                                htmlFor="billing-same-as-shipping"
                            >
                                Same as Shipping Address
                            </label>
                        </div>
                    </div>
                ) : null}

                <div className="col-12">
                    <div
                        className="p-3 rounded d-flex align-items-start"
                        style={{ backgroundColor: "#e7f3ff", gap: "12px" }}
                    >
                        <CreditCard className="text-primary mt-1" size={20} />
                        <div>
                            <h3
                                className="fw-semibold"
                                style={{
                                    color: "#1e3a8a",
                                    fontSize: "1rem",
                                    marginBottom: "4px",
                                }}
                            >
                                Billing Information
                            </h3>
                            <p
                                className="mb-0"
                                style={{ color: "#1e40af", fontSize: "0.875rem" }}
                            >
                                Please provide your billing details for the order.
                            </p>
                        </div>
                    </div>
                </div>

                <div className="col-12 col-md-6">
                    <InputField
                        id="billing-first-name"
                        label="First Name"
                        required
                        value={value.firstName}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("firstName")}
                        error={nameHasComma(value.firstName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.firstName : undefined}
                        onChange={(e) => patchField({ firstName: e.target.value })}
                    />
                </div>
                <div className="col-12 col-md-6">
                    <InputField
                        id="billing-last-name"
                        label="Last Name"
                        required
                        value={value.lastName}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("lastName")}
                        error={nameHasComma(value.lastName) ? NAME_COMMA_ERROR : showFieldErrors ? fieldErrors?.lastName : undefined}
                        onChange={(e) => patchField({ lastName: e.target.value })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="billing-company-name"
                        label="Company Name"
                        value={value.companyName}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("companyName")}
                        error={showFieldErrors ? fieldErrors?.companyName : undefined}
                        onChange={(e) => patchField({ companyName: e.target.value })}
                    />
                </div>

                <div className="col-12 mb-3">
                    <label className="form-label">
                        Country<span className="text-danger ms-1">*</span>
                        {labelHint("country")}
                    </label>
                    <select
                        id="billing-country"
                        value={String(value.country ?? "")}
                        disabled={fieldsLocked}
                        onChange={(e) => patchField({ country: e.target.value })}
                        className={`form-select${showFieldErrors && fieldErrors?.country ? " is-invalid" : ""}`}
                    >
                        <option value="">Select a country</option>
                        {countryNames.map((country) => (
                            <option key={country} value={country}>{country}</option>
                        ))}
                    </select>
                    {showFieldErrors && fieldErrors?.country ? (
                        <div className="text-danger small mt-1">{fieldErrors.country}</div>
                    ) : null}
                </div>

                <div className="col-12 col-md-6 mb-3">
                    <label className="form-label">
                        {value.country === "United States" ? "State" : "Province"}{" "}
                        <span className="text-danger ms-1">*</span>
                        {labelHint("province")}
                    </label>
                    <select
                        id="billing-province"
                        value={String(value.province ?? "")}
                        disabled={fieldsLocked}
                        onChange={(e) => patchField({ province: e.target.value })}
                        className={`form-select${showFieldErrors && fieldErrors?.province ? " is-invalid" : ""}`}
                    >
                        <option value="">
                            {value.country === "United States"
                                ? "Select a state"
                                : "Select a province"}
                        </option>
                        {regionOptions.map((s) => (
                            <option key={s.state_code} value={s.state_code}>
                                {s.name}
                            </option>
                        ))}
                    </select>
                    {showFieldErrors && fieldErrors?.province ? (
                        <div className="text-danger small mt-1">{fieldErrors.province}</div>
                    ) : null}
                </div>
                <div className="col-12 col-md-6">
                    <InputField
                        id="billing-city"
                        label="City"
                        required
                        value={value.city}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("city")}
                        error={showFieldErrors ? fieldErrors?.city : undefined}
                        onChange={(e) => patchField({ city: e.target.value })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="billing-street"
                        label="Street Address"
                        required
                        value={value.streetAddress}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("streetAddress")}
                        error={showFieldErrors ? fieldErrors?.streetAddress : undefined}
                        onChange={(e) =>
                            patchField({ streetAddress: e.target.value })
                        }
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="billing-postal"
                        label="Postal Code"
                        required
                        value={value.postalCode}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("postalCode")}
                        error={showFieldErrors ? fieldErrors?.postalCode : undefined}
                        onChange={(e) => patchField({ postalCode: e.target.value })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="billing-phone"
                        label="Phone"
                        required
                        value={value.phone}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("phone")}
                        error={showFieldErrors ? fieldErrors?.phone : undefined}
                        onChange={(e) => patchField({ phone: formatPhone(e.target.value) })}
                    />
                </div>
                <div className="col-12">
                    <InputField
                        id="billing-email"
                        label="Email"
                        required
                        value={value.email}
                        disabled={fieldsLocked}
                        labelExtra={labelHint("email")}
                        error={showFieldErrors ? fieldErrors?.email : undefined}
                        onChange={(e) => patchField({ email: e.target.value })}
                    />
                </div>

                {showUpdateDefaultPrompt || showSetDefaultPrompt ? (
                    <div className="col-12">
                        <CustomerChoicePrompt
                            id="billing-attach-address-to-customer"
                            message={
                                showUpdateDefaultPrompt ?
                                    "Also update the customer default billing address to the above?" :
                                    "Set this as the customer's default billing address for future orders?"
                            }
                            checked={Boolean(value.attachAddressToCustomer)}
                            onCheckedChange={(checked) =>
                                onChange((prev) => ({
                                    ...prev,
                                    attachAddressToCustomer: checked,
                                }))
                            }
                        />
                    </div>
                ) : null}

                <div className="col-12">
                    <div className="d-flex justify-content-end">
                        <button
                            ref={nextButtonRef}
                            type="button"
                            className="btn btn-primary px-4"
                            style={
                                emphasizeNext ?
                                    {
                                        boxShadow:
                                            "0 0 0 0.25rem rgba(13, 110, 253, 0.45)",
                                    } :
                                    undefined
                            }
                            onClick={onNext}
                        >
                            Next
                        </button>
                    </div>
                </div>
            </div>
        </Accordion>
    )
}
